'use strict';

export const LOCALES = ['bg', 'en'];
export const DEFAULT_LOCALE = 'bg';

/** If a localized map was accidentally JSON.stringified, parse it back. */
const coerceLocalizedValue = value => {
	if (typeof value !== 'string') return value;
	const trimmed = value.trim();
	if (!trimmed || trimmed[0] !== '{') return value;
	try {
		const parsed = JSON.parse(trimmed);
		if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
			const keys = Object.keys(parsed);
			if (keys.some(k => LOCALES.indexOf(k) > -1)) return parsed;
		}
	} catch (err) {
		/* keep original string */
	}
	return value;
};

export function resolveLocalized(value, locale, fallbacks = LOCALES) {
	const loc = (locale || DEFAULT_LOCALE).toString().slice(0, 2).toLowerCase();
	if (value == null) return '';
	value = coerceLocalizedValue(value);
	if (typeof value === 'string') return value;
	if (typeof value === 'object' && !Array.isArray(value)) {
		const direct = value[loc];
		if (direct != null && String(direct).trim() !== '') return String(direct);
		for (let i = 0; i < fallbacks.length; i++) {
			const k = fallbacks[i];
			if (k === loc) continue;
			const v = value[k];
			if (v != null && String(v).trim() !== '') return String(v);
		}
		const keys = Object.keys(value);
		for (let i = 0; i < keys.length; i++) {
			const v = value[keys[i]];
			if (v != null && String(v).trim() !== '') return String(v);
		}
		return '';
	}
	return String(value);
}

/** Collapse map → string when 0/1 filled or all equal; else keep locale map. */
export function normalizeLocalized(value, locales = LOCALES) {
	if (value == null) return '';
	if (typeof value === 'string') return value;
	if (typeof value !== 'object' || Array.isArray(value)) return '';
	const filled = locales
		.map(l => [l, String(value[l] != null ? value[l] : '').trim()])
		.filter(([, v]) => v);
	if (filled.length === 0) return '';
	if (filled.length === 1) return filled[0][1];
	const texts = filled.map(([, v]) => v);
	if (texts.every(t => t === texts[0])) return texts[0];
	return Object.fromEntries(filled);
}

/**
 * Write text for one locale into a LocalizedString.
 * Plain string stays string until a locale diverges, then expands to a map.
 */
export function writeLocalized(current, locale, text, locales = LOCALES) {
	const loc = (locale || DEFAULT_LOCALE).toString().slice(0, 2).toLowerCase();
	const t = text == null ? '' : String(text);
	if (current == null || current === '') return t;
	if (typeof current === 'string') {
		if (t === current) return current;
		const map = {};
		locales.forEach(l => {
			map[l] = l === loc ? t : current;
		});
		return map;
	}
	if (typeof current === 'object' && !Array.isArray(current)) {
		return Object.assign({}, current, {[loc]: t});
	}
	return t;
}

export function normalizeI18nPair(en, bg) {
	return normalizeLocalized({en, bg}, ['en', 'bg']);
}

export function formatLocalizedBilingual(value) {
	if (value == null || value === '') return '';
	if (typeof value === 'string') return String(value).trim();
	if (typeof value === 'object' && !Array.isArray(value)) {
		const en = resolveLocalized(value, 'en');
		const bg = resolveLocalized(value, 'bg');
		if (en && bg && String(en).trim() !== String(bg).trim()) {
			return String(en).trim() + ' / ' + String(bg).trim();
		}
		return (en || bg || '').trim();
	}
	return String(value).trim();
}

export function normalizeDocI18nFields(doc, i18nFields, locales = LOCALES) {
	if (!doc || !i18nFields || !i18nFields.length) return doc;
	const out = Object.assign({}, doc);
	i18nFields.forEach(path => {
		if (String(path).indexOf('.') !== -1) return;
		if (out[path] !== undefined) {
			out[path] = normalizeLocalized(out[path], locales);
		}
	});
	return out;
}
